import { Money } from '@principle-theorem/accounting';
import { type IGetRecordResponse } from '@principle-theorem/practice-migrations/interfaces';
import {
  type ISODateType,
  type Timezone,
  toISODate,
} from '@principle-theorem/shared';
import { compact, countBy, first, groupBy, partition } from 'lodash';
import {
  type ExactAppointment,
  ExactAppointmentStatus,
} from '../../../source/entities/patient-appointment';
import { type ExactTreatment } from '../../../source/entities/patient-treatments';
import {
  EXACT_DEFAULT_PLAN_KEY,
  ExactStepIdentity,
} from './exact-step-identity';

export enum ExactStepKind {
  Completed = 'completed',
  Booked = 'booked',
  Planned = 'planned',
  Undated = 'undated',
}

export interface IExactAppointmentStepLink {
  appointmentUid: string;
  stepSourceIdentifier: string;
}

export interface IExactStepBlueprint {
  sourceIdentifier: string;
  planKey: string;
  kind: ExactStepKind;
  date?: ISODateType;
  treatments: IGetRecordResponse<ExactTreatment>[];
  /**
   * The appointment the source data points this step at. A step keeps it for
   * its id and name even when another step wins the appointment.
   */
  assignedAppointment?: IGetRecordResponse<ExactAppointment>;
  /** The appointment linked to this step; no other step is bound to it. */
  boundAppointment?: IGetRecordResponse<ExactAppointment>;
  /**
   * True when the binding comes from the treatment's own `appointment_ids`,
   * rather than a provider match or the same-day fallback.
   */
  boundByValidatedLink: boolean;
}

export interface IExactStepPlan {
  steps: IExactStepBlueprint[];
  appointmentLinks: IExactAppointmentStepLink[];
}

interface IStepGroupBase {
  planKey: string;
  /** Sorted by `treatment_id`. */
  treatments: IGetRecordResponse<ExactTreatment>[];
}

interface ICompletedStepGroup extends IStepGroupBase {
  kind: ExactStepKind.Completed;
  date: ISODateType;
  assignedAppointment?: IGetRecordResponse<ExactAppointment>;
  hasLinkedTreatment: boolean;
}

interface IBookedStepGroup extends IStepGroupBase {
  kind: ExactStepKind.Booked;
  date: ISODateType;
  assignedAppointment: IGetRecordResponse<ExactAppointment>;
}

interface IPlannedStepGroup extends IStepGroupBase {
  kind: ExactStepKind.Planned;
  date: ISODateType;
  visitSequence: string;
}

interface IUndatedStepGroup extends IStepGroupBase {
  kind: ExactStepKind.Undated;
}

type StepGroup =
  | ICompletedStepGroup
  | IBookedStepGroup
  | IPlannedStepGroup
  | IUndatedStepGroup;

type StepBindings = Map<StepGroup, IGetRecordResponse<ExactAppointment>>;

interface IDatedAppointment {
  appointment: IGetRecordResponse<ExactAppointment>;
  localDate: ISODateType;
}

interface IPlanningContext {
  sourcePatientId: string;
  /** Sorted by `compareExactAppointments`. */
  appointments: IDatedAppointment[];
}

interface ICompletedAssignment {
  treatment: IGetRecordResponse<ExactTreatment>;
  appointment?: IGetRecordResponse<ExactAppointment>;
  isLinked: boolean;
}

interface IBindingCandidate {
  group: StepGroup;
  appointmentUid: string;
  sourceIdentifier: string;
  hasValidatedLink: boolean;
  providerMatchCount: number;
  treatmentCount: number;
  totalFee: number;
}

const ineligibleStatuses = [
  ExactAppointmentStatus.Cancelled,
  ExactAppointmentStatus.Failed,
];
const defaultVisitSequence = '0';
const undatedStepSortDate = '9999-12-31';
const digitsOnlyPattern = /^\d+$/;
const leadingZerosPattern = /^0+/;

export function compareExactAppointments(
  firstAppointment: IGetRecordResponse<ExactAppointment>,
  secondAppointment: IGetRecordResponse<ExactAppointment>
): number {
  const firstStart = firstAppointment.record.translations.appointmentDateTime;
  const secondStart = secondAppointment.record.translations.appointmentDateTime;
  return (
    firstStart.seconds - secondStart.seconds ||
    firstStart.nanoseconds - secondStart.nanoseconds ||
    compareText(
      firstAppointment.data.appointment_id,
      secondAppointment.data.appointment_id
    )
  );
}

/**
 * Decides every Exact treatment step of a patient and the one appointment each
 * step is linked to. Every ordering is total, so the plan never depends on the
 * order of the inputs, and an appointment is never bound to more than one step.
 */
export function planExactTreatmentSteps(
  sourcePatientId: string,
  treatments: IGetRecordResponse<ExactTreatment>[],
  appointments: IGetRecordResponse<ExactAppointment>[],
  timezone: Timezone
): IExactStepPlan {
  const context: IPlanningContext = {
    sourcePatientId,
    appointments: toDatedAppointments(appointments, timezone),
  };
  const [completedTreatments, plannedTreatments] = partition(
    sortTreatments(treatments),
    (treatment) => treatment.record.translations.completedDate
  );
  const groups: StepGroup[] = [
    ...groupCompletedTreatments(completedTreatments, context),
    ...groupPlannedTreatments(plannedTreatments, context),
  ];
  const bindings = bindUnresolvedGroups(
    groups,
    bindAssignedGroups(groups, context),
    context
  );
  const steps = groups
    .map((group) => toBlueprint(group, bindings.get(group), sourcePatientId))
    .sort(compareSteps);
  assertUniqueStepIdentifiers(steps);
  const appointmentLinks = buildAppointmentLinks(steps);

  return {
    steps,
    appointmentLinks,
  };
}

function toDatedAppointments(
  appointments: IGetRecordResponse<ExactAppointment>[],
  timezone: Timezone
): IDatedAppointment[] {
  return [...appointments].sort(compareExactAppointments).map(
    (appointment): IDatedAppointment => ({
      appointment,
      localDate: toISODate(
        appointment.record.translations.appointmentDateTime,
        timezone
      ),
    })
  );
}

function sortTreatments(
  treatments: IGetRecordResponse<ExactTreatment>[]
): IGetRecordResponse<ExactTreatment>[] {
  return [...treatments].sort((firstTreatment, secondTreatment) =>
    compareText(
      firstTreatment.data.treatment_id,
      secondTreatment.data.treatment_id
    )
  );
}

function planKeyOf(treatment: IGetRecordResponse<ExactTreatment>): string {
  return ExactStepIdentity.planKey(treatment.data.treatment_plan_id);
}

function isTreatedBy(
  treatment: IGetRecordResponse<ExactTreatment>,
  appointment: IGetRecordResponse<ExactAppointment>
): boolean {
  const providerCode = treatment.data.provider_code;
  return (
    !!providerCode && providerCode === appointment.data.practitioner_initials
  );
}

function findEligibleAppointments(
  completedDate: ISODateType,
  context: IPlanningContext
): IGetRecordResponse<ExactAppointment>[] {
  return context.appointments
    .filter(
      ({ appointment, localDate }) =>
        localDate === completedDate &&
        !ineligibleStatuses.includes(appointment.data.appointment_status)
    )
    .map(({ appointment }) => appointment);
}

function groupCompletedTreatments(
  treatments: IGetRecordResponse<ExactTreatment>[],
  context: IPlanningContext
): ICompletedStepGroup[] {
  return Object.entries(groupBy(treatments, planKeyOf)).flatMap(
    ([planKey, planTreatments]) =>
      Object.entries(
        groupBy(
          planTreatments,
          (treatment) => treatment.record.translations.completedDate
        )
      ).flatMap(([completedDate, sameDayTreatments]) =>
        groupSameDayCompletedTreatments(
          planKey,
          completedDate,
          sameDayTreatments,
          context
        )
      )
  );
}

/**
 * Treatments with evidence for an appointment get a step per appointment.
 * Treatments without evidence join the earliest of those steps, or share one
 * step when the plan has none that day.
 */
function groupSameDayCompletedTreatments(
  planKey: string,
  completedDate: ISODateType,
  treatments: IGetRecordResponse<ExactTreatment>[],
  context: IPlanningContext
): ICompletedStepGroup[] {
  const eligibleAppointments = findEligibleAppointments(completedDate, context);
  const assignments = treatments.map((treatment) =>
    assignCompletedTreatment(treatment, eligibleAppointments)
  );
  const assignedAppointments = eligibleAppointments.filter((appointment) =>
    assignments.some((assignment) => assignment.appointment === appointment)
  );

  if (!assignedAppointments.length) {
    return [
      {
        kind: ExactStepKind.Completed,
        planKey,
        date: completedDate,
        treatments,
        hasLinkedTreatment: false,
      },
    ];
  }

  const unresolvedTreatments = assignments
    .filter((assignment) => !assignment.appointment)
    .map((assignment) => assignment.treatment);

  return assignedAppointments.map((appointment, index) => {
    const appointmentAssignments = assignments.filter(
      (assignment) => assignment.appointment === appointment
    );
    const joiningTreatments = index === 0 ? unresolvedTreatments : [];
    return {
      kind: ExactStepKind.Completed,
      planKey,
      date: completedDate,
      treatments: sortTreatments([
        ...appointmentAssignments.map((assignment) => assignment.treatment),
        ...joiningTreatments,
      ]),
      assignedAppointment: appointment,
      hasLinkedTreatment: appointmentAssignments.some(
        (assignment) => assignment.isLinked
      ),
    };
  });
}

/**
 * A treatment's own `appointment_ids` wins. Otherwise it takes its provider's
 * earliest appointment that day, as `eligibleAppointments` is in start order.
 */
function assignCompletedTreatment(
  treatment: IGetRecordResponse<ExactTreatment>,
  eligibleAppointments: IGetRecordResponse<ExactAppointment>[]
): ICompletedAssignment {
  const linkedAppointment = eligibleAppointments.find(
    (appointment) =>
      appointment.data.appointment_id === treatment.data.appointment_ids
  );
  if (linkedAppointment) {
    return { treatment, appointment: linkedAppointment, isLinked: true };
  }

  const earliestProviderAppointment = eligibleAppointments.find((appointment) =>
    isTreatedBy(treatment, appointment)
  );
  return {
    treatment,
    appointment: earliestProviderAppointment,
    isLinked: false,
  };
}

function groupPlannedTreatments(
  treatments: IGetRecordResponse<ExactTreatment>[],
  context: IPlanningContext
): StepGroup[] {
  return Object.entries(groupBy(treatments, planKeyOf)).flatMap(
    ([planKey, planTreatments]) => {
      const [bookedTreatments, unbookedTreatments] = partition(
        planTreatments,
        (treatment) => findBookedAppointment(treatment, context)
      );
      const [datedTreatments, undatedTreatments] = partition(
        unbookedTreatments,
        (treatment) => treatment.record.translations.plannedDate
      );
      return [
        ...buildBookedGroups(planKey, bookedTreatments, context),
        ...buildPlannedGroups(planKey, datedTreatments),
        ...buildUndatedGroups(planKey, undatedTreatments),
      ];
    }
  );
}

function findBookedAppointment(
  treatment: IGetRecordResponse<ExactTreatment>,
  context: IPlanningContext
): IDatedAppointment | undefined {
  return context.appointments.find(
    ({ appointment }) =>
      appointment.data.appointment_id === treatment.data.appointment_ids
  );
}

function buildBookedGroups(
  planKey: string,
  treatments: IGetRecordResponse<ExactTreatment>[],
  context: IPlanningContext
): IBookedStepGroup[] {
  return context.appointments.flatMap(
    ({ appointment, localDate }): IBookedStepGroup[] => {
      const bookedTreatments = treatments.filter(
        (treatment) =>
          treatment.data.appointment_ids === appointment.data.appointment_id
      );
      if (!bookedTreatments.length) {
        return [];
      }
      return [
        {
          kind: ExactStepKind.Booked,
          planKey,
          date:
            first(bookedTreatments)?.record.translations.plannedDate ??
            localDate,
          treatments: bookedTreatments,
          assignedAppointment: appointment,
        },
      ];
    }
  );
}

function buildPlannedGroups(
  planKey: string,
  treatments: IGetRecordResponse<ExactTreatment>[]
): IPlannedStepGroup[] {
  return Object.entries(
    groupBy(
      treatments,
      (treatment) => treatment.record.translations.plannedDate
    )
  ).flatMap(([plannedDate, sameDateTreatments]) =>
    Object.entries(
      groupBy(
        sameDateTreatments,
        (treatment) => treatment.data.visit_sequence ?? defaultVisitSequence
      )
    ).map(
      ([visitSequence, visitTreatments]): IPlannedStepGroup => ({
        kind: ExactStepKind.Planned,
        planKey,
        date: plannedDate,
        visitSequence,
        treatments: visitTreatments,
      })
    )
  );
}

function buildUndatedGroups(
  planKey: string,
  treatments: IGetRecordResponse<ExactTreatment>[]
): IUndatedStepGroup[] {
  if (!treatments.length) {
    return [];
  }
  return [{ kind: ExactStepKind.Undated, planKey, treatments }];
}

function getAssignedAppointment(
  group: StepGroup
): IGetRecordResponse<ExactAppointment> | undefined {
  switch (group.kind) {
    case ExactStepKind.Completed:
    case ExactStepKind.Booked:
      return group.assignedAppointment;
    default:
      return;
  }
}

function hasValidatedLink(group: StepGroup): boolean {
  switch (group.kind) {
    case ExactStepKind.Booked:
      return true;
    case ExactStepKind.Completed:
      return group.hasLinkedTreatment;
    default:
      return false;
  }
}

function bindAssignedGroups(
  groups: StepGroup[],
  context: IPlanningContext
): StepBindings {
  const candidates = compact(
    groups.map((group) => toBindingCandidate(group, context.sourcePatientId))
  );
  return new Map(
    context.appointments.flatMap(
      ({
        appointment,
      }): [StepGroup, IGetRecordResponse<ExactAppointment>][] => {
        const winner = first(
          candidates
            .filter(
              (candidate) =>
                candidate.appointmentUid === appointment.data.appointment_id
            )
            .sort(compareBindingCandidates)
        );
        return winner ? [[winner.group, appointment]] : [];
      }
    )
  );
}

function toBindingCandidate(
  group: StepGroup,
  sourcePatientId: string
): IBindingCandidate | undefined {
  const appointment = getAssignedAppointment(group);
  if (!appointment) {
    return;
  }
  return {
    group,
    appointmentUid: appointment.data.appointment_id,
    sourceIdentifier: buildStepUid(group, undefined, sourcePatientId),
    hasValidatedLink: hasValidatedLink(group),
    providerMatchCount: group.treatments.filter((treatment) =>
      isTreatedBy(treatment, appointment)
    ).length,
    treatmentCount: group.treatments.length,
    totalFee: Money.amount(
      Money.sum(
        group.treatments.map((treatment) => treatment.data.total_amount)
      )
    ),
  };
}

function compareBindingCandidates(
  firstCandidate: IBindingCandidate,
  secondCandidate: IBindingCandidate
): number {
  return (
    compareTrueFirst(
      firstCandidate.hasValidatedLink,
      secondCandidate.hasValidatedLink
    ) ||
    secondCandidate.providerMatchCount - firstCandidate.providerMatchCount ||
    secondCandidate.treatmentCount - firstCandidate.treatmentCount ||
    secondCandidate.totalFee - firstCandidate.totalFee ||
    comparePlanKeys(
      firstCandidate.group.planKey,
      secondCandidate.group.planKey
    ) ||
    compareTrueFirst(
      firstCandidate.group.kind === ExactStepKind.Completed,
      secondCandidate.group.kind === ExactStepKind.Completed
    ) ||
    compareText(
      firstCandidate.sourceIdentifier,
      secondCandidate.sourceIdentifier
    )
  );
}

/**
 * Completed groups without an assigned appointment take the earliest eligible
 * appointment that day that no other step is bound to yet.
 */
function bindUnresolvedGroups(
  groups: StepGroup[],
  assignedBindings: StepBindings,
  context: IPlanningContext
): StepBindings {
  const bindings: StepBindings = new Map(assignedBindings);
  const boundAppointmentUids = new Set(
    [...bindings.values()].map((appointment) => appointment.data.appointment_id)
  );
  const unresolvedGroups = groups
    .filter(
      (group): group is ICompletedStepGroup =>
        group.kind === ExactStepKind.Completed && !group.assignedAppointment
    )
    .sort(
      (firstGroup, secondGroup) =>
        comparePlanKeys(firstGroup.planKey, secondGroup.planKey) ||
        compareText(firstGroup.date, secondGroup.date)
    );

  for (const group of unresolvedGroups) {
    const appointment = findEligibleAppointments(group.date, context).find(
      (eligibleAppointment) =>
        !boundAppointmentUids.has(eligibleAppointment.data.appointment_id)
    );
    if (appointment) {
      bindings.set(group, appointment);
      boundAppointmentUids.add(appointment.data.appointment_id);
    }
  }

  return bindings;
}

function buildStepUid(
  group: StepGroup,
  boundAppointment: IGetRecordResponse<ExactAppointment> | undefined,
  sourcePatientId: string
): string {
  switch (group.kind) {
    case ExactStepKind.Completed:
      return ExactStepIdentity.completedStepUid(
        sourcePatientId,
        group.planKey,
        group.date,
        (group.assignedAppointment ?? boundAppointment)?.data.appointment_id
      );
    case ExactStepKind.Booked:
      return ExactStepIdentity.bookedStepUid(
        sourcePatientId,
        group.planKey,
        group.assignedAppointment.data.appointment_id
      );
    case ExactStepKind.Planned:
      return ExactStepIdentity.plannedStepUid(
        sourcePatientId,
        group.planKey,
        group.date,
        group.visitSequence
      );
    default:
      return ExactStepIdentity.undatedStepUid(sourcePatientId, group.planKey);
  }
}

function toBlueprint(
  group: StepGroup,
  boundAppointment: IGetRecordResponse<ExactAppointment> | undefined,
  sourcePatientId: string
): IExactStepBlueprint {
  return {
    sourceIdentifier: buildStepUid(group, boundAppointment, sourcePatientId),
    planKey: group.planKey,
    kind: group.kind,
    date: group.kind === ExactStepKind.Undated ? undefined : group.date,
    treatments: group.treatments,
    assignedAppointment: getAssignedAppointment(group),
    boundAppointment,
    boundByValidatedLink: boundAppointment ? hasValidatedLink(group) : false,
  };
}

function compareSteps(
  firstStep: IExactStepBlueprint,
  secondStep: IExactStepBlueprint
): number {
  return (
    compareText(
      firstStep.date ?? undatedStepSortDate,
      secondStep.date ?? undatedStepSortDate
    ) || compareText(firstStep.sourceIdentifier, secondStep.sourceIdentifier)
  );
}

function assertUniqueStepIdentifiers(steps: IExactStepBlueprint[]): void {
  const duplicateIdentifier = first(
    Object.entries(countBy(steps, (step) => step.sourceIdentifier))
      .filter(([, count]) => count > 1)
      .map(([sourceIdentifier]) => sourceIdentifier)
  );
  if (duplicateIdentifier) {
    throw new Error(
      `Duplicate treatment step identifier: ${duplicateIdentifier}`
    );
  }
}

function buildAppointmentLinks(
  steps: IExactStepBlueprint[]
): IExactAppointmentStepLink[] {
  return steps
    .flatMap((step): IExactAppointmentStepLink[] =>
      step.boundAppointment
        ? [
            {
              appointmentUid: step.boundAppointment.data.appointment_id,
              stepSourceIdentifier: step.sourceIdentifier,
            },
          ]
        : []
    )
    .sort(
      (firstLink, secondLink) =>
        compareText(firstLink.appointmentUid, secondLink.appointmentUid) ||
        compareText(
          firstLink.stepSourceIdentifier,
          secondLink.stepSourceIdentifier
        )
    );
}

/**
 * Named plans come before the default plan; keys made only of digits come
 * before other keys and compare by numeric value; anything still equal (such
 * as '7' and '07') falls back to plain string order.
 */
function comparePlanKeys(firstKey: string, secondKey: string): number {
  const firstIsDigitsOnly = digitsOnlyPattern.test(firstKey);
  const secondIsDigitsOnly = digitsOnlyPattern.test(secondKey);
  return (
    compareTrueFirst(
      firstKey !== EXACT_DEFAULT_PLAN_KEY,
      secondKey !== EXACT_DEFAULT_PLAN_KEY
    ) ||
    compareTrueFirst(firstIsDigitsOnly, secondIsDigitsOnly) ||
    (firstIsDigitsOnly && secondIsDigitsOnly
      ? compareDigitMagnitudes(firstKey, secondKey)
      : 0) ||
    compareText(firstKey, secondKey)
  );
}

/**
 * Compares by significant length, then text, without Number or BigInt, so plan
 * ids too long for a number still sort by magnitude.
 */
function compareDigitMagnitudes(
  firstDigits: string,
  secondDigits: string
): number {
  const firstSignificant = firstDigits.replace(leadingZerosPattern, '');
  const secondSignificant = secondDigits.replace(leadingZerosPattern, '');
  return (
    firstSignificant.length - secondSignificant.length ||
    compareText(firstSignificant, secondSignificant)
  );
}

function compareTrueFirst(firstValue: boolean, secondValue: boolean): number {
  return Number(secondValue) - Number(firstValue);
}

function compareText(firstText: string, secondText: string): number {
  if (firstText < secondText) {
    return -1;
  }
  if (firstText > secondText) {
    return 1;
  }
  return 0;
}
